import { action, observable, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';

import { ActivityResponse, MessageActivityResponse } from 'ui/types';

import RootStore from 'stores/Root';
import API from 'utils/api';
import { AnswerStats, InspectionTaskLinkType, SelectedAnswerType } from 'utils/api/modules/workflows/workflows.types';
import { TaskListResponse } from 'utils/api/modules/tasks/tasks.types';
import { EventType } from 'utils/events/constants';
import { ActionEvent } from 'utils/events/types';

import AnswerActivities from './AnswerActivities';
import AnswerTasks from './AnswerTasks';

interface MessageActivityPayload extends MessageActivityResponse {
  inspectionId: string;
  questionId: string;
}
interface TaskActivityResponse extends ActivityResponse {
  inspectionId?: string;
  questionId?: string;
}

export default class AnswerDetails {
  rootStore: RootStore;

  api: typeof API;

  inspectionId: string;

  questionId: string;

  @observable activities: AnswerActivities;

  @observable tasks: AnswerTasks;

  @observable answer: SelectedAnswerType;

  @observable stats?: AnswerStats;

  constructor(
    rootStore: RootStore,
    api: typeof API,
    inspectionId: string,
    questionId: string,
    data: SelectedAnswerType,
    stats: AnswerStats
  ) {
    makeObservable(this);
    this.rootStore = rootStore;
    this.api = api;
    this.inspectionId = inspectionId;
    this.questionId = questionId;
    this.answer = data;
    this.stats = stats;

    // Register before creating AnswerActivities so that deletedFile handler
    // runs before AnswerActivities.onFileDeleted clears the activities array
    eventBus.register(this);

    this.activities = new AnswerActivities(rootStore, api, inspectionId, questionId);
    this.tasks = new AnswerTasks(rootStore, api, inspectionId, questionId);
  }

  @subscribe(EventType.AddedMessage)
  @action
  addedMessage({ payload }: ActionEvent<MessageActivityPayload>): void {
    if (
      this.stats !== undefined &&
      payload.inspectionId === this.inspectionId &&
      payload.questionId === this.questionId
    ) {
      this.stats.comments += 1;
    }
  }

  @subscribe(EventType.DeletedMessage)
  @action
  deletedMessage({ payload }: ActionEvent<MessageActivityPayload>): void {
    if (
      this.stats !== undefined &&
      payload.inspectionId === this.inspectionId &&
      payload.questionId === this.questionId
    ) {
      this.stats.comments -= 1;
    }
  }

  @subscribe(EventType.CreatedTask)
  @subscribe(EventType.CreatedTaskAnswerTask)
  @action
  createdTask({ payload }: ActionEvent<TaskListResponse>): void {
    if (
      this.stats !== undefined &&
      payload.links.some(
        (link) =>
          link.instance.uuid === this.inspectionId &&
          (link.extraData as InspectionTaskLinkType).questionId === this.questionId
      )
    ) {
      this.stats.tasks += 1;
    }
  }

  @subscribe(EventType.AddedFile)
  @action
  createdFile({ payload }: ActionEvent<TaskActivityResponse>): void {
    if (
      this.stats !== undefined &&
      payload.inspectionId === this.inspectionId &&
      payload.questionId === this.questionId
    ) {
      this.stats.files += 1;
    }
  }

  @subscribe(EventType.DeletedFile)
  @action
  deletedFile({ payload }: ActionEvent<TaskActivityResponse & { fileId?: string }>): void {
    if (this.stats === undefined || payload.inspectionId !== this.inspectionId) {
      return;
    }

    const matchByQuestion = payload.questionId === this.questionId;
    const matchByFile = payload.fileId && this.activities.activities?.some((a) => a.uuid === payload.fileId);

    if (matchByQuestion || matchByFile) {
      this.stats.files -= 1;
    }
  }

  @action
  setAnswer(answer: SelectedAnswerType): void {
    this.answer = answer;
  }
}
